
import HomeIcon from '~/components/ui/icons/HomeIcon.vue';
import PortfolioIcon from '~/components/ui/icons/PortfolioIcon.vue';
import { mapActions, mapGetters } from 'vuex';
import { appURLs, inAppRedirect } from '@/utils/route-redirects';
import LearnIcon from '~/components/ui/icons/LearnIcon.vue';
import BellIcon from '~/components/ui/icons/BellIcon.vue';
import { ComponentIds } from '~/utils/componentIds';
import SearchIcon from '~/components/ui/icons/SearchIcon.vue';
import { capitalizeFirstLetter } from '@/utils/string-formatter';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';
import { debounce } from 'lodash';

export default {
  name: 'NavbarMobile',
  components: {
    HomeIcon,
    PortfolioIcon,
    LearnIcon,
    BellIcon,
    SearchIcon,
  },
  beforeRouteLeave() {
    this.hideSearchPopup();
  },
  data() {
    return {
      appURLs,
      iconSize: '22',
      iconWidth: 1.8,
      activeIndicator: {
        ready: false,
        style: {},
        pressed: false,
        debouncedUpdate: debounce(this.updateActiveIndicator, 150),
      },
      pressedTab: {
        index: -1, // tab under the finger, -1 = none
        startTime: 0,
        releaseTimeout: null,
      },
    };
  },
  computed: {
    ...mapGetters('notifications', ['notificationCount']),
    ...mapGetters('messaging', ['messageNotificationCount']),
    ...mapGetters('authentication', [
      'me',
      'profile',
      'isConnected',
      'profileId',
      'isEmailConfirmed',
    ]),
    ...mapGetters('profile', ['profile', 'profileLoading']),
    ...mapGetters('search', ['searchPopupShown']),
    sanitizedNotificationCount() {
      return this.notificationCount ?? 0;
    },
    sanitizedMessageNotificationCount() {
      return this.messageNotificationCount ?? 0;
    },
    appUrlsLocalized() {
      return {
        feed: this.localePath(appURLs.feed()),
        portfolios: this.localePath(appURLs.portfolios()),
        market: this.localePath(appURLs.markets()),
        messages: this.localePath(appURLs.messages()),
        notifications: this.localePath(appURLs.notification_center()),
        search: this.localePath(appURLs.search()),
        guides: this.localePath(appURLs.academy()),
      };
    },

    isFeedActive() {
      return this.$route.name?.includes(appURLs.feed());
    },
    isPortfoliosActive() {
      return this.$route.name?.includes(appURLs.portfolios());
    },
    isGuidesActive() {
      return this.$route.name?.includes(appURLs.academy());
    },
    isSearchActive() {
      return (
        this.$route.name?.includes(appURLs.searchResults()) ||
        this.searchPopupShown
      );
    },
    isNotificationsActive() {
      return this.$route.name?.includes(appURLs.notification_center());
    },
    /**
     * Index of the tab that matches the current route/search state, -1 if none match.
     */
    activeTabIndex() {
      if (this.isFeedActive) return 0;
      if (this.isPortfoliosActive) return 1;
      if (this.isGuidesActive) return 2;
      if (this.isSearchActive) return 3;
      if (this.isNotificationsActive) return 4;
      return -1;
    },
    /**
     * Index of the tab under which active indicator should be displayed
     * While the user has a finger down on a tab, that tab (pressedTab.index) takes
     * priority for instant visual feedback; otherwise it follows activeTabIndex
     */
    activeTabIndicatorIndex() {
      return this.pressedTab.index >= 0
        ? this.pressedTab.index
        : this.activeTabIndex;
    },

    searchbarId() {
      return ComponentIds.SearchbarId;
    },
  },
  watch: {
    activeTabIndicatorIndex() {
      this.$nextTick(this.updateActiveIndicator);
    },
    activeTabIndex() {
      // route changed, indicator can go back to following the active tab
      this.clearPressedTab();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateActiveIndicator();
      // wait a frame before turning on the transition, otherwise it slides in from the left on load
      requestAnimationFrame(() => {
        this.activeIndicator.ready = true;
      });
    });
    window.addEventListener('resize', this.activeIndicator.debouncedUpdate);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.activeIndicator.debouncedUpdate);
    this.activeIndicator.debouncedUpdate.cancel();
    clearTimeout(this.pressedTab.releaseTimeout);
  },
  methods: {
    ...mapActions('search', ['showSearchPopup', 'hideSearchPopup']),
    updateActiveIndicator() {
      const nav = this.$refs.navbar;
      if (!nav) return;
      const items = nav.querySelectorAll('.navbar-mobile-item');
      const el = items[this.activeTabIndicatorIndex];
      if (this.activeTabIndicatorIndex < 0 || !el) {
        this.activeIndicator.style = {
          ...this.activeIndicator.style,
          opacity: 0,
        };
        return;
      }
      this.activeIndicator.style = {
        opacity: 1,
        width: `${el.offsetWidth}px`,
        transform: `translateX(${el.offsetLeft}px)`,
      };
    },
    clearPressedTab() {
      this.pressedTab.index = -1;
    },
    onNavTouchStart(e) {
      const item = e.target.closest('.navbar-mobile-item');
      if (!item) return;
      clearTimeout(this.pressedTab.releaseTimeout);
      this.pressedTab.index = Number(item.dataset.tabIndex);
      this.activeIndicator.pressed = true;
      this.pressedTab.startTime = Date.now();
    },
    onNavTouchEnd() {
      // keep the press effect for at least 220ms, otherwise a fast tap barely shows it
      const MIN_PRESS_MS = 220;
      const elapsed = Date.now() - this.pressedTab.startTime;
      const releasePress = () => {
        this.activeIndicator.pressed = false;
        // same tab, route won't change, so reset it here instead of waiting on the watcher
        // eliminates multiple tab indicator switches between Home and Portfolio tab, after we refresh the home feed
        if (this.pressedTab.index === this.activeTabIndex) {
          this.clearPressedTab();
        }
      };
      clearTimeout(this.pressedTab.releaseTimeout);
      if (elapsed >= MIN_PRESS_MS) {
        releasePress();
      } else {
        this.pressedTab.releaseTimeout = setTimeout(
          releasePress,
          MIN_PRESS_MS - elapsed
        );
      }
    },
    onNavTouchCancel() {
      clearTimeout(this.pressedTab.releaseTimeout);
      this.activeIndicator.pressed = false;
      this.clearPressedTab();
    },
    async reloadAndScrollUp() {
      this.hideSearchPopup();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await this.$store.dispatch('home/getFeed', { noSkeletons: true });
    },
    navigateToUrl(route, value) {
      this.raiseClickMenuPrincipalEvent(value);
      inAppRedirect(route);
    },
    raiseClickMenuPrincipalEvent(value) {
      eventManager.raiseClickMenuPrincipalEvent({
        text: 'MobileBottomNavbar | ' + capitalizeFirstLetter(value),
      });
    },
  },
};
